<script setup lang="ts">
/* =============================================================
   VESSEL — DETAIL
   =============================================================
   Every field on the row, and the only place a vessel can be
   edited or deleted from — the same rule as BerthDetailPage, and
   for the same reason: you are looking at the whole record when
   you decide to destroy it.

   The three dimensions are always present. Unlike a berth's size
   limits there is no "not stated" here; the columns are NOT NULL
   because a vessel is entered by the company that operates it.
   See supabase/migrations/20260919090628_create_vessels.sql.
   ============================================================= */

import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import DetailList from '@/components/ui/DetailList.vue'
import type { DetailRow } from '@/components/ui/DetailList.vue'
import { routes, to } from '@/config/routes'
import { deleteVessel, getVessel } from '@/lib/vessels'
import { useVesselTypes } from '@/lib/vesselTypes'
import { DataError } from '@/lib/errors'
import BookingsPanel from '@/components/BookingsPanel.vue'
import type { Vessel } from '@/lib/types'

const route = useRoute()
const router = useRouter()
const { vesselTypeLabel } = useVesselTypes()

const vessel = ref<Vessel | null>(null)
const loading = ref(true)
const loadError = ref('')

const id = computed(() => Number(route.params.id))

async function load(): Promise<void> {
  loading.value = true
  loadError.value = ''
  try {
    vessel.value = await getVessel(id.value)
  } catch (e) {
    loadError.value = e instanceof DataError ? e.message : 'Could not load the vessel.'
  } finally {
    loading.value = false
  }
}

onMounted(load)
// Navigating between two detail pages reuses this component, so the id can
// change without a remount.
watch(id, load)

const rows = computed<DetailRow[]>(() => {
  const v = vessel.value
  if (!v) return []
  return [
    { label: 'Name', value: v.name },
    // The embed is null only when the reader cannot see `companies` — see the
    // note on Vessel.company. A signed-in admin always gets a name.
    v.company
      ? { label: 'Company', value: v.company.name, to: to.company(v.company.id) }
      : { label: 'Company', value: String(v.company_id), mono: true },
    { label: 'Type', value: vesselTypeLabel(v.type) },
    { label: 'Length', value: `${v.length_m} m`, mono: true },
    { label: 'Beam', value: `${v.width_m} m`, mono: true },
    { label: 'Draft', value: `${v.draft_m} m`, mono: true },
    { label: 'ID', value: String(v.id), mono: true },
    { label: 'Created', value: new Date(v.created_at).toLocaleString('en-GB') },
  ]
})

/* --- delete ------------------------------------------------------------- */

const confirming = ref(false)
const confirm = useTemplateRef<InstanceType<typeof ConfirmDialog>>('confirm')

async function onDeleteConfirmed(): Promise<void> {
  const v = vessel.value
  if (!v) return
  try {
    await deleteVessel(v.id)
    confirming.value = false
    await router.push(routes.vessels)
  } catch (e) {
    confirm.value?.fail(e instanceof DataError ? e.message : 'Could not delete the vessel.')
  }
}
</script>

<template>
  <p v-if="loading" class="page-status">Loading…</p>

  <template v-else-if="vessel">
    <PageHeader
      :title="vessel.name"
      :description="`${vesselTypeLabel(vessel.type)}${vessel.company ? ` · ${vessel.company.name}` : ''}`"
    >
      <template #actions>
        <Button variant="ghost" icon="chevronRight" :to="routes.vessels" class="back-link">
          All vessels
        </Button>
        <Button variant="secondary" icon="edit" :to="to.vesselEdit(vessel.id)">Edit</Button>
        <Button variant="danger" icon="trash" @click="confirming = true">Delete</Button>
      </template>
    </PageHeader>

    <div class="sections">
      <Panel>
        <DetailList :rows="rows" />
      </Panel>

      <BookingsPanel
        :filter="{ vesselId: vessel.id }"
        :hide="['vessel']"
        description="For this vessel, newest arrival first."
      />
    </div>

    <ConfirmDialog
      ref="confirm"
      :open="confirming"
      title="Delete vessel"
      :message="`“${vessel.name}” will be permanently removed. This cannot be undone.`"
      @close="confirming = false"
      @confirmed="onDeleteConfirmed"
    />
  </template>

  <template v-else>
    <Alert v-if="loadError" tone="danger" class="page-alert">{{ loadError }}</Alert>
    <EmptyState
      v-else
      icon="ship"
      title="Vessel not found"
      description="It may have been deleted, or the link may be wrong."
    >
      <template #action>
        <Button :to="routes.vessels">All vessels</Button>
      </template>
    </EmptyState>
  </template>
</template>

<style scoped>
.sections {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
}
</style>
